import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { parseErrorMessage, throwIfResNotOk } from "@/lib/queryClient";

// Mirrors server/lib/funnel.ts FunnelReport (aggregates only — no account data).
interface FunnelCounts {
  signedUp: number;
  cameBack: number;
  padsplitConnected: number;
  propertyAdded: number;
  wizardDone: number;
  cardAdded: number;
  paid: number;
  lifetime: number;
  /** Stripe charges minus refunds for this cohort, in cents (billing_cash_ledger). */
  cashCollectedCents?: number;
}
interface FunnelCohort extends FunnelCounts {
  weekStart: string;
  partial: boolean;
}
export interface FunnelReport {
  range: { from: string; to: string };
  cohorts: FunnelCohort[];
  totals: FunnelCounts;
  excluded: Record<"demo" | "admin" | "internal" | "test" | "team_member", number>;
  payingTotals: { monthly: number; annual: number; lifetime: number; pm: number; comped: number; total: number };
  cashCollectedCents: number | null;
  cashCollectedNote: string;
  generatedAt: string;
}

const WEEK_OPTIONS = ["4", "8", "12", "26", "52"] as const;
const CUSTOM = "custom";

const STEP_COLUMNS: Array<{ key: Exclude<keyof FunnelCounts, "signedUp" | "cashCollectedCents">; label: string }> = [
  { key: "cameBack", label: "Came back" },
  { key: "padsplitConnected", label: "Connected PadSplit" },
  { key: "propertyAdded", label: "First property" },
  { key: "wizardDone", label: "Setup done" },
  { key: "cardAdded", label: "Card added" },
  { key: "paid", label: "Paid" },
  { key: "lifetime", label: "Lifetime" },
];

const PAYING_TILES: Array<{ key: keyof FunnelReport["payingTotals"]; label: string }> = [
  { key: "monthly", label: "Monthly" },
  { key: "annual", label: "Annual" },
  { key: "lifetime", label: "Lifetime" },
  { key: "pm", label: "PM" },
  { key: "comped", label: "Comped" },
  { key: "total", label: "Total" },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function pct(n: number, d: number): string {
  return d > 0 ? `${Math.round((n / d) * 100)}%` : "–";
}

function centsToUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

export function funnelQueryString(mode: string, from: string, to: string): string {
  if (mode === CUSTOM && DATE_RE.test(from)) {
    const params = new URLSearchParams({ from });
    if (DATE_RE.test(to)) params.set("to", to);
    return params.toString();
  }
  return new URLSearchParams({ weeks: mode === CUSTOM ? "12" : mode }).toString();
}

function CountCell({ n, of }: { n: number; of: number }) {
  return (
    <TableCell className="text-right whitespace-nowrap">
      {n.toLocaleString()} <span className="text-xs text-muted-foreground">{pct(n, of)}</span>
    </TableCell>
  );
}

/** Admin → Funnel: weekly signup cohorts for outside account owners (B9). */
export function FunnelPanel() {
  const [mode, setMode] = useState<string>("12");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const qs = funnelQueryString(mode, from, to);

  const funnelQuery = useQuery<FunnelReport>({
    queryKey: ["/api/admin/funnel", qs],
    queryFn: async () => {
      const res = await fetch(`/api/admin/funnel?${qs}`, { credentials: "include" });
      await throwIfResNotOk(res);
      return (await res.json()) as FunnelReport;
    },
  });
  const data = funnelQuery.data;
  const excludedTotal = data ? Object.values(data.excluded).reduce((s, n) => s + n, 0) : 0;

  return (
    <div className="space-y-6" data-testid="panel-admin-funnel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Funnel</h3>
          <p className="text-sm text-muted-foreground">
            Weekly signup cohorts (Monday start, UTC) for outside account owners. Internal domains, test emails,
            demo, admin and staff accounts, and team members are excluded.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild data-testid="button-funnel-download-csv">
          <a href={`/api/admin/funnel.csv?${qs}`} download>
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </a>
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="funnel-range" className="text-sm">Range</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger id="funnel-range" className="w-44" data-testid="select-funnel-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEK_OPTIONS.map((w) => (
                <SelectItem key={w} value={w}>Last {w} weeks</SelectItem>
              ))}
              <SelectItem value={CUSTOM}>Custom dates</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === CUSTOM && (
          <>
            <div className="space-y-1">
              <Label htmlFor="funnel-from" className="text-sm">From</Label>
              <Input id="funnel-from" type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="input-funnel-from" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="funnel-to" className="text-sm">To</Label>
              <Input id="funnel-to" type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="input-funnel-to" />
            </div>
          </>
        )}
      </div>

      {funnelQuery.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : funnelQuery.isError || !data ? (
        <p className="text-sm text-destructive" data-testid="text-funnel-error">
          {parseErrorMessage(funnelQuery.error, "Couldn't load the funnel.")}
        </p>
      ) : (
        <>
          <Card className="p-4 space-y-3" data-testid="card-funnel-paying-totals">
            <h4 className="font-semibold">Paying accounts today</h4>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              {PAYING_TILES.map((t) => (
                <div key={t.key} className="rounded-md border px-3 py-2">
                  <div className="text-xs text-muted-foreground">{t.label}</div>
                  <div className="text-xl font-semibold" data-testid={`text-funnel-paying-${t.key}`}>
                    {data.payingTotals[t.key].toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="text-funnel-cash">
              Cash collected:{" "}
              {data.cashCollectedCents == null ? data.cashCollectedNote : centsToUsd(data.cashCollectedCents)}
            </p>
          </Card>

          <div className="overflow-x-auto">
            <Table data-testid="table-funnel-cohorts">
              <TableHeader>
                <TableRow>
                  <TableHead>Signup week</TableHead>
                  <TableHead className="text-right">Signed up</TableHead>
                  {STEP_COLUMNS.map((c) => (
                    <TableHead key={c.key} className="text-right">{c.label}</TableHead>
                  ))}
                  <TableHead className="text-right">Cash collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.cohorts.map((c) => (
                  <TableRow key={c.weekStart} data-testid={`row-funnel-cohort-${c.weekStart}`}>
                    <TableCell className="whitespace-nowrap">
                      {c.weekStart}
                      {c.partial && <span className="ml-1 text-xs text-muted-foreground">(partial)</span>}
                    </TableCell>
                    <TableCell className="text-right">{c.signedUp.toLocaleString()}</TableCell>
                    {STEP_COLUMNS.map((col) => (
                      <CountCell key={col.key} n={c[col.key]} of={c.signedUp} />
                    ))}
                    <TableCell className="text-right">{centsToUsd(c.cashCollectedCents ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold" data-testid="row-funnel-totals">
                  <TableCell>
                    {data.range.from} to {data.range.to}
                  </TableCell>
                  <TableCell className="text-right" data-testid="text-funnel-total-signups">
                    {data.totals.signedUp.toLocaleString()}
                  </TableCell>
                  {STEP_COLUMNS.map((col) => (
                    <CountCell key={col.key} n={data.totals[col.key]} of={data.totals.signedUp} />
                  ))}
                  <TableCell className="text-right">{centsToUsd(data.totals.cashCollectedCents ?? 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <p className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="text-funnel-excluded">
            <Filter className="h-3 w-3" />
            Excluded in this range: {excludedTotal} ({data.excluded.internal} internal, {data.excluded.test} test,{" "}
            {data.excluded.demo} demo, {data.excluded.admin} admin, {data.excluded.team_member} team members).
          </p>
        </>
      )}
    </div>
  );
}
